import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * pm-auto-add-players-modal
 * 
 * Componente Lit per il modale di aggiunta automatica giocatori.
 * Gestisce la selezione della quantità tramite slider interattivo e opzioni
 * di bilanciamento skill e pagamento.
 * 
 * @example
 * <pm-auto-add-players-modal
 *   tournament-id="123"
 *   available-players-count="50"
 *   slots-remaining="16"
 *   current-participants="8"
 *   max-participants="24"
 *   action-url="/simulations/tournaments/123/add-random-existing/">
 * </pm-auto-add-players-modal>
 */
@customElement('pm-auto-add-players-modal')
export class PmAutoAddPlayersModal extends LitElement {
    // Properties
    @property({ type: Number, attribute: 'tournament-id' }) tournamentId: number = 0;
    @property({ type: Number, attribute: 'available-players-count' }) availablePlayersCount: number = 0;
    @property({ type: Number, attribute: 'slots-remaining' }) slotsRemaining: number = 0;
    @property({ type: Number, attribute: 'current-participants' }) currentParticipants: number = 0;
    @property({ type: Number, attribute: 'max-participants' }) maxParticipants: number = 0;
    @property({ type: String, attribute: 'action-url' }) actionUrl: string = '';

    // State
    @state() private playerCount: number = 0;
    @state() private balanceSkills: boolean = true;
    @state() private markAsPaid: boolean = false;

    static styles = css`
        :host {
            display: block;
        }

        .alert {
            padding: 0.75rem 1rem;
            margin-bottom: 1rem;
            border: 1px solid transparent;
            border-radius: 0.375rem;
        }

        .alert-info {
            color: #055160;
            background-color: #cff4fc;
            border-color: #b6effb;
        }

        [data-bs-theme="dark"] .alert-info {
            color: #6edff6;
            background-color: rgba(13, 202, 240, 0.15);
            border-color: rgba(13, 202, 240, 0.3);
        }

        .alert ul {
            margin-bottom: 0;
            margin-top: 0.5rem;
            padding-left: 1.5rem;
        }

        .form-label {
            margin-bottom: 0.5rem;
            font-weight: 500;
            display: block;
        }

        .form-range {
            width: 100%;
            height: 1.5rem;
            padding: 0;
            background-color: transparent;
            appearance: none;
        }

        .form-range::-webkit-slider-thumb {
            width: 1.5rem;
            height: 1.5rem;
            margin-top: -0.5rem;
            background-color: #0d6efd;
            border: 0;
            border-radius: 1rem;
            cursor: pointer;
            appearance: none;
        }

        .form-range::-webkit-slider-runnable-track {
            width: 100%;
            height: 0.5rem;
            color: transparent;
            cursor: pointer;
            background-color: #dee2e6;
            border-color: transparent;
            border-radius: 1rem;
        }

        [data-bs-theme="dark"] .form-range::-webkit-slider-runnable-track {
            background-color: #495057;
        }

        .slider-labels {
            display: flex;
            justify-content: space-between;
            margin-top: 0.5rem;
        }

        .slider-value {
            text-align: center;
        }

        .badge {
            display: inline-block;
            padding: 0.35em 0.65em;
            font-size: 0.75em;
            font-weight: 700;
            line-height: 1;
            color: #fff;
            text-align: center;
            white-space: nowrap;
            vertical-align: baseline;
            border-radius: 0.375rem;
        }

        .badge.bg-success {
            background-color: #198754;
        }

        .badge.fs-6 {
            font-size: 1rem !important;
        }

        .text-muted {
            color: #6c757d !important;
        }

        .options-box {
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            padding: 0.75rem;
            margin-top: 1.5rem;
        }

        [data-bs-theme="dark"] .options-box {
            border-color: #495057;
        }

        .options-box h6 {
            margin: 0 0 0.75rem 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .form-check {
            margin-bottom: 0.5rem;
            padding-left: 1.5em;
        }

        .form-check:last-child {
            margin-bottom: 0;
        }

        .form-check-input {
            width: 1.25rem;
            height: 1.25rem;
            margin-top: 0;
            margin-left: -1.5em;
            cursor: pointer;
        }

        .form-check-label {
            cursor: pointer;
            font-weight: 500;
        }

        .form-text {
            margin-top: 0.25rem;
            font-size: 0.875em;
            color: #6c757d;
            display: block;
        }

        .footer-buttons {
            display: flex;
            gap: 0.5rem;
            justify-content: flex-end;
            margin-top: 1.5rem;
        }

        .btn {
            display: inline-block;
            padding: 0.375rem 0.75rem;
            font-size: 1rem;
            font-weight: 400;
            line-height: 1.5;
            text-align: center;
            text-decoration: none;
            vertical-align: middle;
            cursor: pointer;
            border: 1px solid transparent;
            border-radius: 0.375rem;
            transition: all 0.15s ease-in-out;
        }

        .btn-secondary {
            color: #fff;
            background-color: #6c757d;
            border-color: #6c757d;
        }

        .btn-secondary:hover {
            background-color: #5c636a;
            border-color: #565e64;
        }

        .btn-success {
            color: #fff;
            background-color: #198754;
            border-color: #198754;
        }

        .btn-success:hover {
            background-color: #157347;
            border-color: #146c43;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        // Inizializza playerCount con il valore appropriato
        this.playerCount = Math.min(this.slotsRemaining, this.availablePlayersCount);
    }

    private handleSliderChange(e: Event) {
        const input = e.target as HTMLInputElement;
        this.playerCount = parseInt(input.value);
    }

    private handleBalanceSkillsChange(e: Event) {
        const input = e.target as HTMLInputElement;
        this.balanceSkills = input.checked;
    }

    private handleMarkAsPaidChange(e: Event) {
        const input = e.target as HTMLInputElement;
        this.markAsPaid = input.checked;
    }

    private handleSubmit(e: Event) {
        e.preventDefault();

        // Crea form e invia
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.actionUrl;

        // CSRF token
        const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement;
        if (csrfToken) {
            const csrfInput = document.createElement('input');
            csrfInput.type = 'hidden';
            csrfInput.name = 'csrfmiddlewaretoken';
            csrfInput.value = csrfToken.value;
            form.appendChild(csrfInput);
        }

        // Player count
        const countInput = document.createElement('input');
        countInput.type = 'hidden';
        countInput.name = 'player_count';
        countInput.value = this.playerCount.toString();
        form.appendChild(countInput);

        // Balance skills
        if (this.balanceSkills) {
            const balanceInput = document.createElement('input');
            balanceInput.type = 'hidden';
            balanceInput.name = 'balance_skills';
            balanceInput.value = 'on';
            form.appendChild(balanceInput);
        }

        // Mark as paid
        if (this.markAsPaid) {
            const paidInput = document.createElement('input');
            paidInput.type = 'hidden';
            paidInput.name = 'mark_as_paid';
            paidInput.value = 'on';
            form.appendChild(paidInput);
        }

        document.body.appendChild(form);
        form.submit();
    }

    private handleCancel() {
        this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    }

    render() {
        const maxValue = Math.min(this.slotsRemaining, this.availablePlayersCount);

        return html`
            <!-- Info disponibilità -->
            <div class="alert alert-info">
                <i class="fas fa-info-circle me-2"></i>
                <strong>Situazione attuale:</strong>
                <ul>
                    <li>${this.availablePlayersCount} giocatori disponibili</li>
                    <li>${this.slotsRemaining} slot liberi nel torneo</li>
                    <li>${this.currentParticipants}/${this.maxParticipants} partecipanti attuali</li>
                </ul>
            </div>

            <!-- Selezione quantità -->
            <div style="margin-bottom: 1.5rem;">
                <label for="player_count" class="form-label">
                    <i class="fas fa-hashtag me-1"></i>Quanti giocatori vuoi aggiungere?
                </label>
                <input 
                    type="range" 
                    class="form-range" 
                    id="player_count"
                    min="1"
                    max="${maxValue}"
                    .value="${this.playerCount.toString()}"
                    @input=${this.handleSliderChange}>
                <div class="slider-labels">
                    <small class="text-muted">1</small>
                    <div class="slider-value">
                        <span class="badge bg-success fs-6">${this.playerCount}</span>
                        <div class="small text-muted">giocatori selezionati</div>
                    </div>
                    <small class="text-muted">${maxValue}</small>
                </div>
            </div>

            <!-- Opzioni aggiuntive -->
            <div class="options-box">
                <h6>
                    <i class="fas fa-cogs me-2"></i>Opzioni di selezione
                </h6>

                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="balance_skills"
                        .checked=${this.balanceSkills}
                        @change=${this.handleBalanceSkillsChange}>
                    <label class="form-check-label" for="balance_skills">
                        Bilanciare i livelli di abilità
                    </label>
                    <div class="form-text">Cerca di equilibrare i livelli tra principianti ed esperti</div>
                </div>

                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="mark_as_paid"
                        .checked=${this.markAsPaid}
                        @change=${this.handleMarkAsPaidChange}>
                    <label class="form-check-label" for="mark_as_paid">
                        Segna come già pagati
                    </label>
                    <div class="form-text">I giocatori aggiunti avranno stato pagamento "pagato"</div>
                </div>
            </div>

            <!-- Footer buttons -->
            <div class="footer-buttons">
                <button 
                    type="button" 
                    class="btn btn-secondary"
                    @click=${this.handleCancel}>
                    <i class="fas fa-times me-2"></i>Annulla
                </button>
                <button 
                    type="button" 
                    class="btn btn-success"
                    @click=${this.handleSubmit}>
                    <i class="fas fa-dice me-2"></i>Aggiungi ${this.playerCount} Giocatori
                </button>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'pm-auto-add-players-modal': PmAutoAddPlayersModal;
    }
}
